import React, { useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { COPY, ARTIFACT_NAMES, FULL_VIEW_PATHS, DEFAULT_COACH_NAME } from '../copy';
import { compactMoney, centsToDollars, monthName, shortDate } from '../format';
import WealthPictureBody from './WealthPictureBody';
import ValuationRangeBody from './ValuationRangeBody';
import ReadinessBody from './ReadinessBody';
import ValueGapBody from './ValueGapBody';
import WealthGoalBody from './WealthGoalBody';
import FinancingScenarioBody from './FinancingScenarioBody';
import DealStructureBody from './DealStructureBody';
import PartnerRecommendationBody from './PartnerRecommendationBody';

const BODIES = {
  am_i_ok: WealthPictureBody,
  valuation_range: ValuationRangeBody,
  readiness_scorecard: ReadinessBody,
  value_gap: ValueGapBody,
  wealth_goal: WealthGoalBody,
  financing_scenario: FinancingScenarioBody,
  deal_structure: DealStructureBody,
  partner_recommendation: PartnerRecommendationBody,
};

export function changeChip(artifact) {
  const change = artifact.change || artifact.data?.change;
  const delta = change?.headline_delta_cents;
  if (!delta) return null;
  const sign = delta > 0 ? '+' : '';
  const since = monthName(change.since);
  return `${sign}${compactMoney(centsToDollars(delta))}${since ? ` since ${since}` : ''}`;
}

const CHIP_TONES = {
  neutral: 'border-border bg-transparent text-muted-foreground',
  orange: 'border-succeedo-orange/30 bg-succeedo-orange/[0.08] text-orange-ink',
  teal: 'border-[hsl(var(--chart-2)/0.3)] bg-[hsl(var(--chart-2)/0.1)] text-teal-ink',
};

function Chip({ children, tone = 'neutral' }) {
  return (
    <span className={`font-label text-[10px] tracking-[0.06em] uppercase py-[3px] px-2 rounded-full border ${CHIP_TONES[tone]}`}>
      {children}
    </span>
  );
}

// The shared frame for every artifact (PRD §6.3): eyebrow + chips, a body
// per kind, the assumptions ("How <coach> worked this out"), completeness,
// and the footer (saved, ask the coach, open full view).
export default function ArtifactCard({ artifact, coachName = DEFAULT_COACH_NAME, onAsk, compact = false }) {
  const [open, setOpen] = useState(false);
  if (!artifact) return null;
  const Body = BODIES[artifact.kind];
  const data = artifact.data || {};
  const assumptions = Array.isArray(data.assumptions) ? data.assumptions : [];
  const completeness = artifact.completeness;
  const delta = changeChip(artifact);
  const fullViewPath = FULL_VIEW_PATHS[artifact.kind];
  const fullView = typeof fullViewPath === 'function' ? fullViewPath(artifact) : fullViewPath;
  const estimate = typeof completeness === 'number' && completeness < 100;

  return (
    <article
      aria-label={ARTIFACT_NAMES[artifact.kind] || artifact.title}
      data-testid={`artifact-${artifact.kind}`}
      className={`bg-card border border-border rounded-2xl flex flex-col gap-5 ${compact ? 'p-[18px]' : 'p-6'}`}
    >
      <header className="flex items-center gap-2 flex-wrap">
        <span className="inline-block w-1.5 h-1.5 shrink-0 rounded-full bg-succeedo-orange shadow-[0_0_8px_hsl(var(--succeedo-orange)/0.6)]" />
        <span className="font-label text-[11px] uppercase tracking-[0.06em] text-muted-foreground">
          {ARTIFACT_NAMES[artifact.kind] || artifact.title}
        </span>
        {estimate && <Chip tone="orange">Estimate</Chip>}
        {artifact.version > 1 && <Chip>v{artifact.version}</Chip>}
        {delta && <Chip tone="teal">{delta}</Chip>}
      </header>

      {Body ? <Body artifact={artifact} data={data} /> : <p className="m-0">{artifact.summary}</p>}

      {assumptions.length > 0 && (
        <div className="border-t border-border pt-1">
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            className="flex items-center justify-between w-full min-h-10 py-1.5 px-0 bg-transparent border-0 text-[14px] text-foreground cursor-pointer"
          >
            <span>{COPY.howWorkedOut(coachName)}</span>
            <ChevronDown
              size={16}
              className={`text-muted-foreground transition-transform duration-200 ease-[ease] ${open ? 'rotate-180' : ''}`}
            />
          </button>
          {open && (
            <ul className="my-1 mx-0 p-0 list-none">
              {assumptions.map((line) => (
                <li key={line} className="py-[9px] px-0 border-t border-border/60 text-[13px] leading-normal text-muted-foreground">
                  {line}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {estimate && (
        <div className="flex flex-col gap-2">
          <div
            role="progressbar"
            aria-valuenow={completeness}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label="How complete this picture is"
            className="h-1.5 rounded-full bg-border overflow-hidden"
          >
            <div
              className="h-full rounded-full bg-gradient-to-r from-succeedo-orange to-[hsl(var(--succeedo-orange-light))]"
              style={{ width: `${completeness}%` }}
            />
          </div>
          <span className="text-[13px] text-muted-foreground">
            <span className="text-foreground tabular-nums">{completeness}% complete.</span>{' '}
            Each answer sharpens it.
          </span>
        </div>
      )}

      <footer className="flex items-center justify-between flex-wrap gap-y-2.5 gap-x-4 border-t border-border pt-4 -mt-1">
        <span className="inline-flex items-center gap-1.5 font-label text-[11px] text-muted-foreground">
          <Check size={12} />
          {COPY.saved(shortDate(artifact.updated_at))}
        </span>
        <div className="flex items-center flex-wrap gap-[18px]">
          {onAsk && (
            <button
              type="button"
              onClick={() => onAsk(artifact)}
              className="bg-transparent border-0 p-0 min-h-8 text-[13px] text-orange-ink cursor-pointer"
            >
              {COPY.askAbout(coachName)} →
            </button>
          )}
          {fullView && (
            <a href={fullView} className="inline-flex items-center min-h-8 text-[13px] text-orange-ink no-underline">
              {COPY.openFullView} →
            </a>
          )}
        </div>
      </footer>
    </article>
  );
}
